/**
 * `?origin_surface=` / `?content_slug=` — which product surface sent a visitor
 * to a job page, carried from the job page to the `/out` apply hop.
 *
 * Why this exists (2026-09-26): blog posts list the jobs they reference, but
 * those links carried no surface at all, so a blog-driven apply was recorded as
 * a plain `detail` apply. The `intel-wedge-kill-criteria` decision
 * (scripts/decisions/decisions-due.json) judges content on whether it produces
 * job traffic, and it was reading zero partly because the path was invisible.
 *
 * Two things are deliberately kept apart:
 *
 *  - ATTRIBUTION (`parseOriginSurfaceParam`): which surface to stamp on the
 *    apply events. Any value in `ORIGIN_SURFACE_PARAMS`.
 *  - PROVENANCE (`isProvenanceOriginSurface`): whether the value may count as
 *    evidence a real person clicked a link we published, in
 *    `assessPartnerClickIntegrity`. That verdict decides whether a partner
 *    click is billable, and a query param is trivially forgeable. Only the
 *    job-alert email qualifies, as before, because an email click arrives with
 *    no referer and this is its only provenance. A blog click is a same-origin
 *    navigation and already carries a same-origin `Referer`, so it needs no
 *    extra provenance, and widening the billing verdict for it would only
 *    help a crawler that appends the param.
 *
 * Pure and dependency-free: imported by the client apply CTA and the server
 * `/out` route alike.
 */

import type { ApplyOriginSurface } from "@/lib/analytics/events";

/** Values `?origin_surface=` may carry. Anything else reads as absent. */
export const ORIGIN_SURFACE_PARAMS = [
	"job_alert_email",
	"blog",
] as const satisfies readonly ApplyOriginSurface[];

export type OriginSurfaceParam = (typeof ORIGIN_SURFACE_PARAMS)[number];

/** Blog slugs are lowercase kebab; anything else is dropped, never echoed. */
const CONTENT_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,159}$/;

export function parseOriginSurfaceParam(
	value: string | null | undefined,
): OriginSurfaceParam | undefined {
	return (ORIGIN_SURFACE_PARAMS as readonly string[]).includes(value ?? "")
		? (value as OriginSurfaceParam)
		: undefined;
}

/**
 * The content slug is only meaningful next to a content surface, and it is a
 * PostHog property, so it is validated rather than passed through.
 */
export function parseContentSlugParam(
	surface: OriginSurfaceParam | undefined,
	value: string | null | undefined,
): string | undefined {
	if (surface !== "blog" || !value) return undefined;
	return CONTENT_SLUG_RE.test(value) ? value : undefined;
}

/** See the module doc: only the email surface is billing provenance. */
export function isProvenanceOriginSurface(
	surface: OriginSurfaceParam | undefined,
): surface is "job_alert_email" {
	return surface === "job_alert_email";
}

/**
 * Append the attribution params to a job-page or `/out` URL. Keeps any
 * existing query string and fragment intact.
 */
export function withOriginSurface(
	url: string,
	surface: OriginSurfaceParam,
	contentSlug?: string,
): string {
	const hashAt = url.indexOf("#");
	const base = hashAt === -1 ? url : url.slice(0, hashAt);
	const hash = hashAt === -1 ? "" : url.slice(hashAt);
	const params = new URLSearchParams({ origin_surface: surface });
	const slug = parseContentSlugParam(surface, contentSlug);
	if (slug) params.set("content_slug", slug);
	return `${base}${base.includes("?") ? "&" : "?"}${params.toString()}${hash}`;
}
